import React, { useState, useEffect } from 'react';
import PartnerSurveyModal from './PartnerSurveyModal';
import axios from 'axios';
import { useSurvey } from '../context/SurveyContext';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { feedbackTabAllowed } from '../constants/thirdPartyPages';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
// TODO: Add Doorly Trustpilot profile URL to env variable
// REACT_APP_TRUSTPILOT_URL=https://www.trustpilot.com/review/godoorly.com
const TRUSTPILOT_URL = process.env.REACT_APP_TRUSTPILOT_URL || 'https://www.trustpilot.com/review/godoorly.com';

// ── Question Components ───────────────────────────────────────────────────────

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  const labels = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
  return (
    <div>
      <div className="flex gap-2 justify-center my-3">
        {[1,2,3,4,5].map(n => (
          <button
            key={n}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(n)}
            style={{
              fontSize: 36,
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              transform: (hovered || value) >= n ? 'scale(1.15)' : 'scale(1)',
              transition: 'transform 0.15s',
              color: (hovered || value) >= n ? '#ffcc00' : '#d1d5db',
              padding: '2px',
            }}
          >★</button>
        ))}
      </div>
      {(hovered || value) > 0 && (
        <p style={{ textAlign: 'center', fontSize: 13, color: '#6b7280' }}>
          {labels[(hovered || value) - 1]}
        </p>
      )}
    </div>
  );
}

function NPSScore({ value, onChange }) {
  const colors = { low: '#ff082f', mid: '#f59e0b', high: '#acf44e' };
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center', margin: '12px 0' }}>
        {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
          const color = n <= 6 ? colors.low : n <= 8 ? colors.mid : colors.high;
          const isSelected = value === n;
          return (
            <button
              key={n}
              onClick={() => onChange(n)}
              style={{
                width: 38, height: 38, borderRadius: 8,
                border: `2px solid ${isSelected ? color : '#e5e7eb'}`,
                backgroundColor: isSelected ? color : 'white',
                color: isSelected ? 'white' : '#374151',
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >{n}</button>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af' }}>
        <span>Not likely</span>
        <span>Extremely likely</span>
      </div>
    </div>
  );
}

function EmojiReaction({ value, onChange }) {
  const options = [
    { emoji: '😞', label: 'Terrible' },
    { emoji: '😕', label: 'Bad' },
    { emoji: '😐', label: 'Okay' },
    { emoji: '🙂', label: 'Good' },
    { emoji: '😊', label: 'Great' },
  ];
  return (
    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', margin: '12px 0' }}>
      {options.map(({ emoji, label }, i) => (
        <button
          key={i}
          onClick={() => onChange(emoji)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
            borderRadius: 8,
            backgroundColor: value === emoji ? '#eff6ff' : 'transparent',
            transform: value === emoji ? 'scale(1.2)' : 'scale(1)',
            transition: 'all 0.15s',
          }}
        >
          <span style={{ fontSize: 32 }}>{emoji}</span>
          <span style={{ fontSize: 10, color: '#6b7280' }}>{label}</span>
        </button>
      ))}
    </div>
  );
}

function MultipleChoice({ options, value, onChange, multi = false }) {
  const toggle = (opt) => {
    if (multi) {
      const arr = value || [];
      onChange(arr.includes(opt) ? arr.filter(x => x !== opt) : [...arr, opt]);
    } else {
      onChange(opt);
    }
  };
  const isSelected = (opt) => multi ? (value || []).includes(opt) : value === opt;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '8px 0' }}>
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => toggle(opt)}
          style={{
            padding: '10px 14px', borderRadius: 10, textAlign: 'left', fontSize: 13,
            border: `2px solid ${isSelected(opt) ? '#3a6ad9' : '#e5e7eb'}`,
            backgroundColor: isSelected(opt) ? '#eff6ff' : 'white',
            color: isSelected(opt) ? '#1d4ed8' : '#374151',
            cursor: 'pointer', fontWeight: isSelected(opt) ? 600 : 400,
            transition: 'all 0.15s',
          }}
        >{opt}</button>
      ))}
    </div>
  );
}

function ShortText({ placeholder, value, onChange, multiLine = false }) {
  const props = {
    value: value || '',
    onChange: e => onChange(e.target.value),
    placeholder,
    style: {
      width: '100%', borderRadius: 10, border: '1.5px solid #e5e7eb',
      padding: '10px 12px', fontSize: 13, outline: 'none',
      resize: multiLine ? 'vertical' : 'none',
      minHeight: multiLine ? 80 : 'auto',
      boxSizing: 'border-box',
    },
  };
  return multiLine ? <textarea {...props} rows={3} /> : <input type="text" {...props} />;
}

function YesNo({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 12, margin: '8px 0' }}>
      <button
        onClick={() => onChange(true)}
        style={{
          flex: 1, padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 700,
          border: '2px solid', cursor: 'pointer', transition: 'all 0.15s',
          borderColor: value === true ? '#3a6ad9' : '#e5e7eb',
          backgroundColor: value === true ? '#3a6ad9' : 'white',
          color: value === true ? 'white' : '#374151',
        }}
      >Yes</button>
      <button
        onClick={() => onChange(false)}
        style={{
          flex: 1, padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 700,
          border: '2px solid', cursor: 'pointer', transition: 'all 0.15s',
          borderColor: value === false ? '#6b7280' : '#e5e7eb',
          backgroundColor: value === false ? '#f3f4f6' : 'white',
          color: '#374151',
        }}
      >No</button>
    </div>
  );
}

// ── Survey Definitions ────────────────────────────────────────────────────────

const SURVEYS = {
  post_application: {
    title: 'How was your application experience?',
    variant: 'popup',
    steps: [
      {
        id: 'ease',
        type: 'star',
        question: 'How easy was it to complete your application?',
      },
      {
        id: 'improve',
        type: 'multi',
        question: 'What could we improve?',
        options: ['Too many questions', 'Confusing instructions', 'Technical issues', 'Nothing, it was great', 'Other'],
        optional: true,
      },
      {
        id: 'feedback',
        type: 'text',
        question: 'Any other feedback?',
        placeholder: 'Tell us more...',
        optional: true,
      },
    ],
  },
  post_document_upload: {
    title: "How's the document upload experience?",
    variant: 'slideout',
    steps: [
      {
        id: 'ease',
        type: 'emoji',
        question: 'How easy was uploading your documents?',
      },
      {
        id: 'need_help',
        type: 'yesno',
        question: 'Would you like help with your documents?',
        conditional: (answers) => answers.ease === '😞' || answers.ease === '😕',
      },
    ],
  },
  post_approval: {
    title: '🎉 You\'re Approved! Tell us about your journey',
    variant: 'popup',
    celebratory: true,
    steps: [
      { id: 'overall', type: 'star', question: 'How would you rate your experience with Doorly so far?' },
      {
        id: 'did_well',
        type: 'multi',
        question: 'What did we do well?',
        options: ['Fast process', 'Easy to use', 'Great communication', 'Helpful team', 'Clear information'],
        optional: true,
        conditional: (answers) => answers.overall >= 4,
      },
      { id: 'testimonial', type: 'text', question: 'Would you like to share your experience? (We may feature it as a testimonial)', placeholder: 'Your experience here...', optional: true, multiLine: true },
      { id: 'recommend', type: 'yesno', question: 'Would you recommend Doorly to a friend or family member?' },
    ],
  },
  post_home_shopping: {
    title: "How's your home search going?",
    variant: 'embedded',
    steps: [
      { id: 'feeling', type: 'emoji', question: 'How are you feeling about the homes available?' },
      {
        id: 'improve',
        type: 'multi',
        question: 'What would improve your search?',
        options: ['More homes in my area', 'Lower prices', 'Better photos', 'More details on listings', 'Filter options', 'Everything is great'],
        optional: true,
      },
    ],
  },
  post_option_period: {
    title: 'How was your option period experience?',
    variant: 'slideout',
    steps: [
      { id: 'rating', type: 'star', question: 'How would you rate the option period process?' },
      { id: 'feedback', type: 'text', question: 'Any feedback on your appraisal package experience?', placeholder: 'Share your thoughts...', optional: true, multiLine: true },
    ],
  },
  post_closing: {
    title: '🏠 Welcome Home! How was your Doorly experience?',
    variant: 'popup',
    celebratory: true,
    steps: [
      { id: 'nps', type: 'nps', question: 'How likely are you to recommend Doorly to someone you know?' },
      { id: 'overall', type: 'star', question: 'How would you rate your overall experience?' },
      {
        id: 'best_part',
        type: 'multi',
        question: 'What was the best part?',
        options: ['Fast closing', 'Easy process', 'Great team', 'Better than traditional mortgage', 'Got the home I wanted'],
        optional: true,
      },
      { id: 'testimonial', type: 'text', question: 'Tell us about your experience (may be used as a testimonial with your permission)', placeholder: 'Your story here...', optional: true, multiLine: true },
    ],
  },
  passive_feedback: {
    title: 'Share your feedback',
    variant: 'slideout',
    steps: [
      { id: 'feeling', type: 'emoji', question: "How are you feeling about your Doorly experience today?" },
      { id: 'thoughts', type: 'text', question: "What's on your mind?", placeholder: 'Tell us anything...', multiLine: true },
      { id: 'follow_up', type: 'yesno', question: 'Would you like us to follow up?', optional: true },
    ],
  },
};

// ── Review Prompt (Trustpilot + Google) ──────────────────────────────────────
function TrustpilotPrompt({ triggerType, onClose }) {
  const { token } = useAuth();

  const handleClick = (platform, url) => {
    window.open(url, '_blank', 'noopener');
    axios.post(`${API}/api/surveys/trustpilot-click`, { trigger_type: triggerType, platform }, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: 16, padding: '28px 32px',
      maxWidth: 440, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🌟</div>
      <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, color: '#111827' }}>
        {triggerType === 'post_closing' ? "You're a homeowner! Share your story" : 'Share your experience online'}
      </h3>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
        {triggerType === 'post_closing'
          ? 'Help other families achieve homeownership by sharing your Doorly experience.'
          : 'Your positive experience helps other families find Doorly. It only takes 60 seconds.'}
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={() => handleClick('trustpilot', TRUSTPILOT_URL)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 20px', background: '#00b67a', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14, minWidth: 160 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          Trustpilot
        </button>
        <button onClick={() => handleClick('google', 'https://g.page/r/CSBYlRbNPwVkEBM/review')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 20px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14, minWidth: 160 }}>
          <svg width="14" height="14" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Google
        </button>
      </div>
      <button onClick={onClose}
        style={{ width: '100%', padding: 10, borderRadius: 10, border: '1.5px solid #e5e7eb', backgroundColor: 'white', color: '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
        Maybe later
      </button>
    </div>
  );
}

// ── Survey Runner ─────────────────────────────────────────────────────────────
function SurveyRunner({ surveyType, onClose, onSubmit }) {
  // 🔑 INSIDE THE GUIDED SHELL, THE SHELL OWNS THE STEP COUNTER (E2E follow-up
  // item 5). The application-experience survey opened over guided step 4 with
  // its own "Step 1 of 3" directly under the shell's "Step 4 of 8" — two
  // counters disagreeing on one screen. The dots stay; the words go.
  const inGuidedShell = useLocation().pathname.startsWith('/apply/guided');
  const survey = SURVEYS[surveyType];
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showTrustpilot, setShowTrustpilot] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!survey) return null;

  const visibleSteps = survey.steps.filter(s => !s.conditional || s.conditional(answers));
  const currentStep = visibleSteps[step];
  const isLast = step === visibleSteps.length - 1;
  const canAdvance = currentStep?.optional || answers[currentStep?.id] != null;

  const handleAnswer = (val) => setAnswers(prev => ({ ...prev, [currentStep.id]: val }));

  const handleNext = async () => {
    if (isLast) {
      const star = answers.overall ?? answers.ease ?? answers.rating ?? null;
      const nps = answers.nps ?? null;
      await onSubmit({
        responses: answers,
        star_rating: typeof star === 'number' ? star : null,
        nps_score: typeof nps === 'number' ? nps : null,
        emoji_reaction: answers.feeling || answers.ease || null,
        would_recommend: answers.recommend ?? null,
        testimonial_text: answers.testimonial || null,
      });
      setSubmitted(true);
      // Show Trustpilot for high ratings
      const highRating = (star >= 4) || (nps >= 9);
      if (highRating && (surveyType === 'post_approval' || surveyType === 'post_closing')) {
        setShowTrustpilot(true);
      } else {
        setTimeout(onClose, 1500);
      }
    } else {
      setStep(s => s + 1);
    }
  };

  if (showTrustpilot) {
    return <TrustpilotPrompt triggerType={surveyType} onClose={onClose} />;
  }

  if (submitted && !showTrustpilot) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🙏</div>
        <h3 style={{ fontWeight: 700, color: '#111827', marginBottom: 8 }}>Thank you!</h3>
        <p style={{ color: '#6b7280', fontSize: 14 }}>Your feedback helps us improve.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Progress dots */}
      {visibleSteps.length > 1 && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 20 }}>
          {visibleSteps.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 20 : 8, height: 8, borderRadius: 4,
              backgroundColor: i <= step ? '#3a6ad9' : '#e5e7eb',
              transition: 'all 0.3s',
            }} />
          ))}
        </div>
      )}

      {!inGuidedShell && (
        <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginBottom: 4 }}>
          Step {step + 1} of {visibleSteps.length}
          {currentStep?.optional && ' (optional)'}
        </p>
      )}
      <h4 style={{ fontWeight: 600, color: '#111827', fontSize: 15, marginBottom: 12, textAlign: 'center', lineHeight: 1.4 }}>
        {currentStep?.question}
      </h4>

      {currentStep?.type === 'star' && <StarRating value={answers[currentStep.id]} onChange={handleAnswer} />}
      {currentStep?.type === 'nps' && <NPSScore value={answers[currentStep.id]} onChange={handleAnswer} />}
      {currentStep?.type === 'emoji' && <EmojiReaction value={answers[currentStep.id]} onChange={handleAnswer} />}
      {currentStep?.type === 'multi' && <MultipleChoice options={currentStep.options} value={answers[currentStep.id]} onChange={handleAnswer} />}
      {currentStep?.type === 'text' && <ShortText placeholder={currentStep.placeholder} value={answers[currentStep.id]} onChange={handleAnswer} multiLine={currentStep.multiLine} />}
      {currentStep?.type === 'yesno' && <YesNo value={answers[currentStep.id]} onChange={handleAnswer} />}

      {/* Show "Contact Support" if they said yes to needing help */}
      {currentStep?.id === 'need_help' && answers.need_help === true && (
        <div style={{ marginTop: 8, padding: '10px', backgroundColor: '#eff6ff', borderRadius: 8 }}>
          <p style={{ fontSize: 12, color: '#1d4ed8', marginBottom: 6 }}>We'd be happy to help!</p>
          <Link to="/messages" onClick={onClose} style={{ color: '#3a6ad9', fontWeight: 600, fontSize: 13 }}>
            Contact Support →
          </Link>
        </div>
      )}

      <button
        onClick={handleNext}
        disabled={!canAdvance}
        style={{
          marginTop: 16, width: '100%', padding: '12px', borderRadius: 10,
          border: 'none', backgroundColor: canAdvance ? '#3a6ad9' : '#e5e7eb',
          color: canAdvance ? 'white' : '#9ca3af',
          fontWeight: 700, fontSize: 14, cursor: canAdvance ? 'pointer' : 'default',
          transition: 'all 0.15s',
        }}
      >
        {isLast ? 'Submit' : 'Next →'}
      </button>

      <button
        onClick={onClose}
        style={{ marginTop: 8, width: '100%', background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', padding: '6px' }}
      >
        Skip for now
      </button>
    </div>
  );
}

// ── Popup Survey ──────────────────────────────────────────────────────────────
function PopupSurvey({ surveyType, title, onClose, onSubmit }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="safe-modal-overlay"
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        animation: 'surveyFadeIn 0.3s ease-out',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="safe-modal-box" style={{
        backgroundColor: 'white', borderRadius: 16, padding: '28px 32px',
        maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        animation: 'surveyScaleIn 0.3s ease-out',
        '--sm-max': '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#3a6ad9', letterSpacing: 1, marginBottom: 4 }}>DOORLY</div>
            <h3 style={{ fontWeight: 700, fontSize: 18, color: '#111827', lineHeight: 1.3, margin: 0 }}>{title}</h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <SurveyRunner surveyType={surveyType} onClose={onClose} onSubmit={onSubmit} />
      </div>
    </div>
  );
}

// ── Slide-Out Survey ──────────────────────────────────────────────────────────
function SlideOutSurvey({ surveyType, title, onClose, onSubmit }) {
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        style={{
          position: 'fixed', right: 0, top: '50%', transform: 'translateY(-50%)',
          backgroundColor: '#3a6ad9', color: 'white', border: 'none', cursor: 'pointer',
          padding: '12px 6px', borderRadius: '8px 0 0 8px', zIndex: 9000,
          writingMode: 'vertical-rl', textOrientation: 'mixed', fontWeight: 700, fontSize: 12,
          letterSpacing: 1,
        }}
      >Feedback</button>
    );
  }

  return (
    <div className="safe-modal-overlay" style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 360, maxWidth: '100vw',
      backgroundColor: 'white', borderLeft: '3px solid #3a6ad9',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.1)', zIndex: 9000,
      display: 'flex', flexDirection: 'column',
      animation: 'surveySlideIn 0.3s ease-out',
      overflowY: 'auto',
    }}>
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#3a6ad9', letterSpacing: 1, marginBottom: 4 }}>DOORLY</div>
          <h3 style={{ fontWeight: 700, fontSize: 16, color: '#111827', margin: 0 }}>{title}</h3>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setMinimized(true)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', color: '#9ca3af', fontSize: 12, padding: '3px 8px' }}>_</button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
      </div>
      <div style={{ padding: '20px', flex: 1 }}>
        <SurveyRunner surveyType={surveyType} onClose={onClose} onSubmit={onSubmit} />
      </div>
    </div>
  );
}

// ── Embedded Micro-Survey ─────────────────────────────────────────────────────
export function EmbeddedMicroSurvey({ surveyType }) {
  const { triggerSurvey, activeSurvey, closeSurvey, submitSurvey } = useSurvey(); // eslint-disable-line no-unused-vars
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const survey = SURVEYS[surveyType];
  if (!show || !survey) return null;

  return (
    <div style={{
      backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12,
      padding: '20px', margin: '20px 0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#3a6ad9', letterSpacing: 1 }}>QUICK SURVEY</div>
          <h4 style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginTop: 4, marginBottom: 0 }}>{survey.title}</h4>
        </div>
        <button onClick={() => setShow(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18 }}>×</button>
      </div>
      <SurveyRunner
        surveyType={surveyType}
        onClose={() => { setShow(false); closeSurvey(true); }}
        onSubmit={async (payload) => { await submitSurvey(payload); setShow(false); }}
      />
    </div>
  );
}

// ── Passive Feedback Tab ──────────────────────────────────────────────────────
export function PassiveFeedbackTab() {
  const { triggerSurvey, activeSurvey } = useSurvey();
  const { user } = useAuth();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // 🚨 THE TAB DID NOTHING ON EVERY DoorlyPro PAGE. triggerSurvey opens with
  // `if (!token || !user) return` — and both come from the BORROWER auth context.
  // A partner is authenticated through useDoorlyProAuth with a different token, so
  // for them those are null and the click returned before anything happened. The
  // button rendered, looked live, and was inert.
  //
  // 🔑 PARTNERS HAVE THEIR OWN SURVEY, AND IT ALREADY WORKS. The dashboard mounts
  // PartnerSurveyModal behind a "Share Feedback" button; what was missing is a way
  // to reach it from anywhere else in the portal. The tab opens that modal rather
  // than trying to push a partner through the borrower survey pipeline, which
  // reads a different table and would have no row for them.
  const [partnerSurveyOpen, setPartnerSurveyOpen] = useState(false);
  const isPartnerArea = location.pathname.startsWith('/doorlypro');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleOpen = () => {
    if (isPartnerArea) { setPartnerSurveyOpen(true); return; }
    triggerSurvey('passive_feedback');
  };

  // 🔑 ONE RULE, IN constants/thirdPartyPages (Jesse, 2026-10-29): only with a signed-in borrower (or, in the partner
  // portal, a signed-in partner); never on a third-party page (VOM, VOR, gift letter, offer review, PSA signing …),
  // whoever is signed in on the device; and, as before, not in the staff area or the guided shell (no side tabs).
  // ⚠️ The partner check stays the token in storage: registration, login and the reset pages are inside /doorlypro but
  // have no partner to survey, and the submit endpoint is behind verifyPartnerToken.
  let partnerSignedIn = false;
  try { partnerSignedIn = !!localStorage.getItem('doorlypro_token'); } catch (_) { partnerSignedIn = false; }
  if (activeSurvey || isMobile) return null;
  if (!feedbackTabAllowed({ pathname: location.pathname, borrowerSignedIn: !!user, partnerSignedIn })) return null;

  if (partnerSurveyOpen) {
    return <PartnerSurveyModal
      onClose={() => setPartnerSurveyOpen(false)}
      onSubmit={() => setTimeout(() => setPartnerSurveyOpen(false), 2500)} />;
  }

  return (
    <button
      onClick={handleOpen}
      style={{
        position: 'fixed', right: 0, top: '50%', transform: 'translateY(-50%)',
        backgroundColor: '#3a6ad9', color: 'white', border: 'none', cursor: 'pointer',
        padding: '12px 6px', borderRadius: '8px 0 0 8px', zIndex: 8000,
        writingMode: 'vertical-rl', textOrientation: 'mixed', fontWeight: 700, fontSize: 12,
        letterSpacing: 1, boxShadow: '-2px 2px 8px rgba(0,0,0,0.15)',
      }}
    >Feedback</button>
  );
}

// ── CSS Animations (injected once) ────────────────────────────────────────────
const STYLE = `
@keyframes surveyFadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes surveyScaleIn { from { transform: scale(0.92); opacity: 0 } to { transform: scale(1); opacity: 1 } }
@keyframes surveySlideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }
`;

// ── Main SurveyOverlay — rendered globally ────────────────────────────────────
export default function SurveyOverlay() {
  const { activeSurvey, closeSurvey, submitSurvey } = useSurvey();

  useEffect(() => {
    if (!document.getElementById('survey-styles')) {
      const s = document.createElement('style');
      s.id = 'survey-styles';
      s.textContent = STYLE;
      document.head.appendChild(s);
    }
  }, []);

  if (!activeSurvey) return null;
  const { type } = activeSurvey;
  const survey = SURVEYS[type];
  if (!survey) return null;

  const handleClose = () => closeSurvey(true);
  const handleSubmit = async (payload) => { await submitSurvey(payload); };

  if (survey.variant === 'slideout') {
    return <SlideOutSurvey surveyType={type} title={survey.title} onClose={handleClose} onSubmit={handleSubmit} />;
  }
  return <PopupSurvey surveyType={type} title={survey.title} onClose={handleClose} onSubmit={handleSubmit} />;
}
